import { onReady } from '../../shared/utils/helpers';
import Base from '../../components/base/base';
import CookieConsent from '../../components/cookie-consent/cookie-consent';
import Header from '../../components/header/header';
import HorizontalScrollSection from '../../components/horizontal-scroll-section/horizontal-scroll-section';
import HorizontalScrollNative from '../../components/horizontal-scroll-native/horizontal-scroll-native';
import VerticalWordScroll from '../../components/vertical-word-scroll/vertical-word-scroll';
import BuyerIntakeForm from '../../components/buyer-intake-form/buyer-intake-form';
import FaqAccordion from '../../components/faq-accordion/faq-accordion';
import './process.scss';

// Toggle the process-steps interaction:
//   true  -> native horizontal scrolling with a styled scrollbar + intro nudge
//   false -> original GSAP/ScrollTrigger pinned horizontal scroll
const USE_NATIVE_SCROLL = true;

interface ProcessPageOptions {
  debug?: boolean;
  [key: string]: any;
}

class ProcessPage extends Base {
  private header: Header;
  private horizontalScrollSection: HorizontalScrollSection | HorizontalScrollNative | null = null;
  private verticalWordScroll: VerticalWordScroll | null = null;
  private faqAccordion: FaqAccordion | null = null;

  constructor(options: ProcessPageOptions = {}) {
    // Pass debug option to Base class
    super(options.debug || false);
    this.debug = options.debug || false;
    
    // Initialize header with same debug setting for consistent logging
    this.header = new Header({ debug: this.debug });
    
    this.init();
  }

  init(): void {
    this.log('ProcessPage initialized');

    // Cookie consent gate. Webflow holds the Meta Pixel until this grants.
    try {
      new CookieConsent({ debug: this.debug });
      this.log('CookieConsent initialized');
    } catch (e) {
      this.log('CookieConsent init failed or skipped', e);
    }

    // Initialize Buyer Intake Form
    try {
      new BuyerIntakeForm({ debug: this.debug });
      this.log('BuyerIntakeForm initialized');
    } catch (e) {
      this.log('BuyerIntakeForm init failed or skipped', e);
    }
    
    // Initialize HorizontalScrollSection if the required elements exist
    this.initializeHorizontalScroll();
    
    // Initialize VerticalWordScroll if the required elements exist
    this.initializeVerticalWordScroll();

    // Initialize FAQ accordion if the required elements exist
    this.initializeFaqAccordion();
  }

  /**
   * Initialize the horizontal scroll section
   * Uses default selectors that match the component's expectations.
   * Picks the native-scroll or GSAP-pinned implementation based on USE_NATIVE_SCROLL.
   */
  initializeHorizontalScroll(): void {
    // Check if the required elements exist in the DOM
    const trigger = document.querySelector('.process-step-height');

    if (!trigger) {
      this.log('HorizontalScroll elements not found, skipping initialization');
      return;
    }

    if (USE_NATIVE_SCROLL) {
      this.log('Initializing HorizontalScrollNative');
      this.horizontalScrollSection = new HorizontalScrollNative({
        debug: this.debug
      });
    } else {
      this.log('Initializing HorizontalScrollSection');
      this.horizontalScrollSection = new HorizontalScrollSection({
        debug: this.debug
      });
    }
  }

  /**
   * Initialize the vertical word scroll section
   * Uses default selectors that match the component's expectations
   */
  initializeVerticalWordScroll(): void {
    // Check if the required elements exist in the DOM
    const trigger = document.querySelector('.onestop__wrapper');
    
    if (trigger) {
      this.log('Initializing VerticalWordScroll');
      this.verticalWordScroll = new VerticalWordScroll({
        debug: this.debug
      });
    } else {
      this.log('VerticalWordScroll elements not found, skipping initialization');
    }
  }

  /**
   * Initialize the FAQ accordion behavior
   */
  initializeFaqAccordion(): void {
    const trigger = document.querySelector('.qa-section');

    if (trigger) {
      this.log('Initializing FaqAccordion');
      // The "Questions, Answered" section renders from the FAQ Groups and FAQs
      // collections, so its classes are the qa-* set rather than the component's
      // faq-* defaults. One list across all four groups keeps the accordion to
      // a single open answer page-wide, not one per group.
      this.faqAccordion = new FaqAccordion({
        debug: this.debug,
        containerSelector: '.qa-section',
        listSelector: '.qa-list',
        itemSelector: '.qa-item',
        headerSelector: '.qa-item-header',
        bodySelector: '.qa-item-answer',
        iconSelector: '.qa-item-icon-wrap',
        // The icon is a gold plus on no background here; only the rotation reads.
        iconOpenBackground: null,
        iconClosedBackground: null
      });
    } else {
      this.log('FaqAccordion elements not found, skipping initialization');
    }
  }

  /**
   * Cleanup method
   */
  destroy(): void {
    if (this.horizontalScrollSection) {
      this.horizontalScrollSection.destroy();
    }
    
    if (this.verticalWordScroll) {
      this.verticalWordScroll.destroy();
    }

    if (this.faqAccordion) {
      this.faqAccordion.destroy();
    }
  }
}

onReady(() => {
  // You can enable debug mode by passing { debug: true }
  new ProcessPage({ debug: true });
});

export default ProcessPage;
